import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { FieldArray } from "react-final-form-arrays";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import type { DropResult } from "@hello-pangea/dnd";
import type { FieldArrayRenderProps } from "react-final-form-arrays";

import { NewBlockDialog } from "./components/NewBlockDialog";
import { EditBlockDialog } from "./components/EditBlockDialog";
import { BlockItem } from "./components/BlockItem";
import AddIcon from "@mui/icons-material/Add";
import type { PlatformBlocksProps, PlatformBlock } from "./types";

const makeOnDragEndFunction =
  (fields: FieldArrayRenderProps<PlatformBlock, HTMLElement>["fields"]) =>
  (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    fields.move(result.source.index, result.destination.index);
  };

/**
 * Главный компонент для управления блоками страницы платформы
 * Поддерживает drag & drop, копирование/вставку, редактирование блоков
 */
export const PlatformBlocks: React.FC<PlatformBlocksProps> = ({
  name,
  label,
  values,
  platformId,
  queries,
}) => {
  const { t } = useTranslation();
  const [newBlockIndex, setNewBlockIndex] = useState<number | undefined>();
  const [editBlock, setEditBlock] = useState<
    { index: number; activeTab?: "SETTINGS" | "VISIBILITY" } | undefined
  >();

  const blocksArray = (name
    .split(".")
    .reduce((p: any, c: string) => p?.[c], values) || []) as PlatformBlock[];

  const onCloseDialog = () => {
    setNewBlockIndex(undefined);
    setEditBlock(undefined);
  };

  const onEditBlock = (
    index: number,
    activeTab?: "SETTINGS" | "VISIBILITY"
  ) => {
    setEditBlock({ index, activeTab });
  };

  const handleCreateAbove = (index: number) => {
    setNewBlockIndex(index);
  };

  const handleCreateBelow = (index: number) => {
    setNewBlockIndex(index + 1);
  };

  const onClickAdd = () => {
    setNewBlockIndex(0);
  };

  return (
    <>
      <FieldArray name={name}>
        {({ fields }) => {
          // Убеждаемся, что fields.value всегда массив
          if (!Array.isArray(fields.value)) {
            fields.value = [];
          }
          const blocks = fields.value as PlatformBlock[];

          const onAddBlockCompleted = (block: PlatformBlock) => {
            if (newBlockIndex !== undefined) {
              // Вставляем блок в указанную позицию
              fields.insert(newBlockIndex, block);
            }
            onCloseDialog();
          };

          const onEditBlockCompleted = (updatedBlock: PlatformBlock) => {
            if (editBlock !== undefined) {
              // Обновляем существующий блок
              fields.update(editBlock.index, updatedBlock);
            }
            onCloseDialog();
          };

          return (
            <>
              <Paper sx={{ p: 2 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="subtitle1" fontWeight={500}>
                    {label}
                  </Typography>
                  <IconButton size="small" onClick={onClickAdd}>
                    <AddIcon />
                  </IconButton>
                </Stack>
              </Paper>

              <Stack sx={{ mt: 1 }} spacing={1}>
                <DragDropContext onDragEnd={makeOnDragEndFunction(fields)}>
                  <Droppable droppableId="droppable">
                    {(provided) => (
                      <Stack
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        spacing={1}
                        useFlexGap
                      >
                        {fields.length === 0 ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ py: 2, textAlign: "center" }}
                          >
                            {t("platformBlocks.list.empty")}
                          </Typography>
                        ) : (
                          fields.map((fieldName, index) => {
                            const block = blocks[index];
                            if (!block) return null;

                            return (
                              <Draggable
                                key={fieldName}
                                draggableId={fieldName}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <BlockItem
                                    block={block}
                                    fieldName={fieldName}
                                    index={index}
                                    isDragging={snapshot.isDragging}
                                    draggableStyle={
                                      provided.draggableProps.style
                                    }
                                    onEdit={onEditBlock}
                                    onDelete={() => fields.remove(index)}
                                    onCreateAbove={
                                      !index ? handleCreateAbove : undefined
                                    }
                                    onCreateBelow={handleCreateBelow}
                                    provided={provided}
                                    snapshot={snapshot}
                                  />
                                )}
                              </Draggable>
                            );
                          })
                        )}
                        {provided.placeholder}
                      </Stack>
                    )}
                  </Droppable>
                </DragDropContext>
              </Stack>
              {newBlockIndex !== undefined && (
                <NewBlockDialog
                  onClose={onCloseDialog}
                  onCompleted={onAddBlockCompleted}
                  platformId={platformId}
                  queries={queries}
                />
              )}
              {editBlock !== undefined && (
                <EditBlockDialog
                  open={true}
                  onClose={onCloseDialog}
                  block={blocksArray[editBlock.index]}
                  onCompleted={onEditBlockCompleted}
                  platformId={platformId}
                  queries={queries}
                  activeTab={editBlock.activeTab}
                />
              )}
            </>
          );
        }}
      </FieldArray>
    </>
  );
};
